import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * Box to write (or fix) a note. Enter saves, Shift+Enter adds a line, Esc
 * cancels.
 */
export const NoteComposer = ({
  initialText = "",
  onSave,
  onCancel,
}: {
  initialText?: string;
  /** Resolves true when saved */
  onSave: (text: string) => Promise<boolean>;
  onCancel: () => void;
}) => {
  const [text, setText] = useState(initialText);
  const [saving, setSaving] = useState(false);
  const value = text.trim();

  const save = async () => {
    if (!value || saving) return;
    setSaving(true);
    const ok = await onSave(value);
    setSaving(false);
    if (ok) setText("");
  };

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-500/40 dark:bg-amber-400/10">
      <Textarea
        autoFocus
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            save();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
          }
        }}
        placeholder="Ej.: Le interesa pero lo habla con su hijo. Llamar el lunes por la tarde."
        className="min-h-20 resize-none border-0 bg-transparent p-1 text-[15px] shadow-none focus-visible:ring-0"
        disabled={saving}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-amber-900/60 dark:text-amber-200/60">
          Enter guarda · Mayús+Enter salto de línea
        </span>
        <div className="flex gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 rounded-full"
            onClick={onCancel}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            className="h-8 rounded-full bg-neutral-900 text-white hover:bg-neutral-800 dark:bg-amber-300 dark:text-neutral-900 dark:hover:bg-amber-200"
            onClick={save}
            disabled={!value || saving}
          >
            {saving ? "Guardando…" : "Guardar nota"}
          </Button>
        </div>
      </div>
    </div>
  );
};
